import { Grid } from "@mui/material";
import ColumnHeader from "./ColumnHeader";
import { useEffect, useState } from "react";
import { ContactUpdatePayload, loadContacts, updateContact } from "../services/connector";
import { Contact, StatusContact, EvaluationStatus } from "../types/Contact";
import CardZone from "./CardZone";
import { Actions } from "../types/Actions";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { boardColumns } from "./configuration/boardColumns";
import ApplicationModal from "./modals/ApplicationModal";

const emptyStatusContacts = boardColumns.reduce((statusMap, column) => {
    statusMap[column.status] = [];
    return statusMap;
}, {} as StatusContact);

interface BoardProps {
    jobId: number;
}

export default function Board({ jobId }: BoardProps) {
    const [displayedApplication, setDisplayedApplication] = useState<
        Contact | undefined
    >(undefined);

    const [currentAction, setCurrentAction] = useState<Actions | undefined>(undefined);

    const [statusContacts, setStatusContacts] = useState(emptyStatusContacts);

    useEffect(() => {
        const fetchContacts = async () => {
            const fetchedContacts = await loadContacts(jobId);

            const mutableStatusContactMap = { ...emptyStatusContacts };
            Object.keys(mutableStatusContactMap).forEach(
                (status) =>
                (mutableStatusContactMap[status] = fetchedContacts.filter(
                    (contact) => contact.evaluation.status === status
                ))
            );
            setStatusContacts(mutableStatusContactMap);
        };

        fetchContacts();
    }, [jobId]);

    const handleCardAction = (contact: Contact, action: Actions) => {
        setDisplayedApplication(contact);
        setCurrentAction(action);
    }

    const handleApplicationDialogClose = () => {
        setDisplayedApplication(undefined);
        setCurrentAction(undefined);
    }

    const handleContactPlainUpdate = (updatedContact: Contact) => {
        // Handles contact update without changing it's status
        const contactStatus = updatedContact.evaluation.status;

        setStatusContacts({ ...statusContacts, [contactStatus]: statusContacts[contactStatus].map(contact => contact.id === updatedContact.id ? updatedContact : contact) });
    }

    const handleContactEvaluationUpdate = async (contact: Contact, payload: ContactUpdatePayload, updatedStatusContacts?: StatusContact) => {
        try {
            const updatedContact = await updateContact(contact, payload);

            const referenceStatusContacts = !updatedStatusContacts ? statusContacts : updatedStatusContacts;

            // Replace in the column
            const updatedStatus = updatedContact.evaluation.status;
            const contacts = [...referenceStatusContacts[updatedStatus]].map(contact => contact.id === updatedContact.id ? updatedContact : contact);

            setStatusContacts({ ...referenceStatusContacts, [updatedStatus]: contacts });

            if (displayedApplication) {
                // Refresh the currently displayed application
                setDisplayedApplication(updatedContact);
            }
        } catch (err) {
            // Rest state in case of error
            setStatusContacts({ ...statusContacts })
        }
    }

    const handleContactStatusUpdate = async (contact: Contact, newStatus: EvaluationStatus) => {
        const oldStatus = contact.evaluation.status;
        
        if (oldStatus !== newStatus) {
            const sourceContacts = [...statusContacts[oldStatus]];
            const destinationContacts = [...statusContacts[newStatus]];

            // Remove from previous column and add to selected column
            const updatedStatusContacts = {
                ...statusContacts,
                [oldStatus]: sourceContacts.filter(sourceContact => sourceContact.id !== contact.id),
                [newStatus]: [...destinationContacts, contact],
            };

            setStatusContacts(updatedStatusContacts);

            // Update remotely
            await handleContactEvaluationUpdate(contact, { status: newStatus }, updatedStatusContacts);
        }
    }

    const handleNavigate = (next: boolean) => {
        if (!displayedApplication) {
            return;
        }

        const flattenedContacts = Object.values(statusContacts).flat();

        const currentIndex = flattenedContacts.findIndex(contact => contact.id === displayedApplication.id);

        if (next) {
            setDisplayedApplication(flattenedContacts[(currentIndex + 1) % flattenedContacts.length]);
        } else {
            const navigateIndex = currentIndex - 1 < 0 ? flattenedContacts.length - 1 : currentIndex - 1;
            setDisplayedApplication(flattenedContacts[navigateIndex]);
        }
    }

    const onDragEnd = async (dragResult: DropResult) => {
        const { source, destination } = dragResult;

        if (!destination) return;

        // Dragged to another column
        if (source.droppableId !== destination.droppableId) {
            const sourceContacts = [...statusContacts[source.droppableId]];
            const destinationContacts = [...statusContacts[destination.droppableId]];

            // Remove from the source column
            const [removed] = sourceContacts.splice(source.index, 1);

            // Add to the destination column
            destinationContacts.splice(destination.index, 0, removed);

            const updatedStatusContacts = {
                ...statusContacts,
                [source.droppableId]: sourceContacts,
                [destination.droppableId]: destinationContacts,
            };

            setStatusContacts(updatedStatusContacts);

            // Update remotely
            await handleContactEvaluationUpdate(removed, { status: destination.droppableId as EvaluationStatus }, updatedStatusContacts);
        }
        // Dragged within the same column
        else {
            const contacts = [...statusContacts[source.droppableId]];

            // Remove the contact at it's previous index
            const [removed] = contacts.splice(source.index, 1);

            // Add it again at the new index
            contacts.splice(destination.index, 0, removed);

            setStatusContacts({
                ...statusContacts,
                [source.droppableId]: contacts,
            });
        }
    };

    return (
        <DragDropContext
            onDragEnd={(dragResult: DropResult) => onDragEnd(dragResult)}
        >
            <Grid container spacing={2}>
                {boardColumns.map((column) => {
                    const columnContacts = statusContacts[column.status];

                    return (
                        <Grid
                            key={column.status}
                            item
                            xs={12 / boardColumns.length}
                            sx={{ display: "flex", flexDirection: "column", minHeight: "600px" }}
                        >
                            <ColumnHeader column={column} contacts={columnContacts.length} />
                            <CardZone
                                onCardAction={handleCardAction}
                                onContactUpdate={handleContactEvaluationUpdate}
                                status={column.status}
                                contacts={columnContacts}
                            />
                        </Grid>
                    );
                })}
                <ApplicationModal contact={displayedApplication}
                    tab={currentAction}
                    onClose={handleApplicationDialogClose}
                    onTabChange={action => setCurrentAction(action)}
                    onContactUpdate={handleContactPlainUpdate}
                    onContactEvaluationUpdate={handleContactEvaluationUpdate}
                    onContactStatusUpdate={handleContactStatusUpdate}
                    onNavigate={handleNavigate}
                />
            </Grid>
        </DragDropContext>
    );
}
